import { forwardRef, useImperativeHandle, useState } from 'react';
import moment from 'moment';
import { EditCommentForm } from '@/components/comment/EditCommentForm';
import { IoIosClose } from 'react-icons/io';
import { env } from '@/utils/configs/env';
import { DeleteComment } from '@/components/comment/DeleteComment';
import { ITestIssueSchema } from '@/api/testIssue';
import { CommentService } from '@/api/comment';
import { Loading } from '@/components/Loading';

type CommentsHistoryProps = {
  testIssue: ITestIssueSchema;
};

export type CommentsHistoryRef = {
  refetch: () => void;
};

const statusStyle = (status: string) => {
  switch (status) {
    case 'NEW':
      return 'bg-blue-100 text-blue-500 border-blue-500';
    case 'OPEN':
      return 'bg-green-100 text-green-500 border-green-500';
    case 'REOPEN':
      return 'bg-yellow-100 text-yellow-500 border-yellow-500';
    case 'CLOSED':
      return 'bg-red-100 text-red-500 border-red-500';
    case 'FIXED':
      return 'bg-purple-100 text-purple-500 border-purple-500';
    case 'INVALID':
      return 'bg-gray-100 text-gray-500 border-gray-500';
    case 'DUPLICATE':
      return 'bg-pink-100 text-pink-500 border-pink-500';
    default:
      return 'bg-black text-white border-black';
  }
};

export const CommentHistory = forwardRef<
  CommentsHistoryRef,
  CommentsHistoryProps
>((props, ref) => {
  const [isOpenImage, setIsOpenImage] = useState<boolean>(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number>(0);
  const [selectedMedias, setSelectedMedias] = useState<Array<{
    id: string;
    name: string;
    path: string;
  }> | null>(null);

  const {
    data: comments,
    isFetching,
    refetch,
  } = CommentService.useAllByTestIssueId(props.testIssue.id);

  const previewUrl = selectedMedias
    ? `${env.apiBaseUrl}/download${selectedMedias[selectedMediaIndex].path}`
    : '';

  useImperativeHandle(ref, () => ({ refetch }));

  const handleShowMedia = (
    medias: Array<{ id: string; name: string; path: string }>,
    index: number,
  ) => {
    setSelectedMedias(medias);
    setSelectedMediaIndex(index);
    setIsOpenImage(true);
  };

  const closeModal = () => {
    setIsOpenImage(false);
    setSelectedMedias(null);
    setSelectedMediaIndex(0);
  };

  const handlePrevMedia = () => {
    setSelectedMediaIndex(prevIndex =>
      prevIndex > 0 ? prevIndex - 1 : selectedMedias!.length - 1,
    );
  };

  const handleNextMedia = () => {
    setSelectedMediaIndex(prevIndex =>
      prevIndex < selectedMedias!.length - 1 ? prevIndex + 1 : 0,
    );
  };

  if (isFetching) {
    return <Loading />;
  }

  return (
    <div className="comments-history">
      <h3 className="font-semibold">Comments History</h3>
      {comments?.length === 0 && (
        <p className="text-gray-500">No comments available.</p>
      )}

      {comments?.map((comment, index) => (
        <div
          key={comment.id}
          className="p-3 my-2 bg-gray-100 border border-gray-500 rounded-md comment-card"
        >
          <div className="flex justify-between">
            <span
              className="text-sm font-bold text-gray-800 bg-gray-200 border border-gray-400 rounded-md"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                lineHeight: '1.5',
                minWidth: '80px',
                textAlign: 'center',
                display: 'inline-block',
              }}
            >
              {comment?.created_by?.full_name}
            </span>
            <div className="flex items-center space-x-6">
              {' '}
              {/* Berikan jarak antara created_at dan tombol edit */}
              <span className="text-sm text-gray-800">
                {moment(comment.updated_at).format('MMMM DD, YYYY HH:mm:ss')}
              </span>
              {/* Buat div untuk tombol edit dan delete agar berdekatan */}
              <div className="flex space-x-1">
                {' '}
                {/* Gunakan space-x-2 untuk memberikan jarak antara tombol */}
                {index === 0 && (
                  <span className="text-gray-500">
                    <EditCommentForm
                      comment={comment}
                      testIssue={props.testIssue}
                      onSuccess={refetch}
                    />
                  </span>
                )}
                <span className="text-gray-500">
                  <DeleteComment id={comment.id!} onSucces={refetch} />
                </span>
              </div>
            </div>
          </div>
          <p className="mt-2">{comment.comment_summary}</p>
          <div className="flex justify-between mt-2">
            {/* Status box */}
            <span
              className={`font-bold uppercase border rounded-md ${statusStyle(
                comment.status,
              )}`}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                lineHeight: '1.5',
                minWidth: '80px',
                textAlign: 'center',
                display: 'inline-block',
              }}
            >
              {comment.status}
            </span>
            {/* Attachments */}
            <span className="text-green-700">
              {(comment?.medias?.length ?? 0) > 0 && (
                <button
                  className="text-blue-500 underline"
                  onClick={() => handleShowMedia(comment?.medias ?? [], 0)} // Show first media
                >
                  Attachments
                </button>
              )}
            </span>
          </div>
        </div>
      ))}

      {/* Modal for media preview */}
      {isOpenImage && selectedMedias && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto bg-black/80">
          <div className="relative flex flex-col items-center justify-center w-full max-h-screen p-4 max-w-7xl">
            <div className="relative flex justify-center w-full">
              {(() => {
                const currentMedia = selectedMedias[selectedMediaIndex];
                const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.exec(
                  currentMedia.name,
                );
                const isVideo = /\.(mp4|webm|mov)$/i.exec(currentMedia.name);

                if (isImage) {
                  return (
                    <img
                      src={previewUrl}
                      className="object-contain max-w-full max-h-screen"
                      alt={currentMedia.name}
                    />
                  );
                }

                if (isVideo) {
                  return (
                    <video width="100%" height="auto" controls>
                      <track kind="captions" />
                      <source src={previewUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  );
                }

                return (
                  <div
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: 'white',
                      fontSize: '30px',
                    }}
                  >
                    <p>Preview not available for this file type</p>
                  </div>
                );
              })()}

              {/* Tombol Close */}
              <button
                className="fixed z-50 p-1 text-2xl transition-all delay-100 bg-white rounded-full hover:bg-lime-200 top-2 right-2"
                onClick={closeModal}
              >
                <IoIosClose />
              </button>

              {/* Tombol Navigasi */}
              {selectedMedias.length > 1 && (
                <>
                  <button
                    className="absolute z-50 p-3 text-white transform -translate-y-1/2 bg-gray-700 rounded-full left-4 top-1/2 hover:bg-gray-500"
                    onClick={handlePrevMedia}
                    style={{ position: 'fixed' }} // Menjaga tombol tetap di kiri
                  >
                    {'<'}
                  </button>
                  <button
                    className="absolute z-50 p-3 text-white transform -translate-y-1/2 bg-gray-700 rounded-full right-4 top-1/2 hover:bg-gray-500"
                    onClick={handleNextMedia}
                    style={{ position: 'fixed' }} // Menjaga tombol tetap di kanan
                  >
                    {'>'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
